import React from 'react';
import { Link } from 'react-router-dom';
import { InfoWindow } from '@react-google-maps/api';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as icon from '../../../media/icons';
import directions from '../../../assets/images/directions.png';
import logo from '../../../media/images/wpLogo.png';
import { useParkingService } from '../../../models/CarPark';

const InfoContainer = styled.div`
  width: 300px;
`;

const ClickableInfo = styled(Link)`
  text-decoration: none !important;
  padding-right: 45px;
  position: relative;
  display: block;

  &:after {
    position: absolute;
    top: 50%;
    right: -12px;
    display: inline-block;
    width: 24px;
    height: 24px;
    border: 1px solid transparent;
    line-height: 24px;
    color: #ffffff;
    pointer-events: none;
    content: '';
    background-image: url(${icon.nextwhite});
    background-repeat: no-repeat;
    background-position: 55% center;
    background-size: 60% auto;
    border-radius: 50%;
    vertical-align: middle;
    background-color: #fd980f;
    transform: translate(-50%, -50%);
  }
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;

  img {
    object-fit: contain;
  }

  &:not(:first-child) {
    padding-left: 10px;
  }
`;

const IconLabel = styled.span`
  display: inline-block;
  width: 100%;
  color: #888888;
  padding-top: 5px;
  font-size: 0.75rem;
  font-weight: normal;
  vertical-align: baseline;
`;

const ServiceIcon = ({ serviceIcon, name }) => {
  return (
    <IconContainer>
      <img
        alt={`${name}_icon`}
        src={`${process.env.REACT_APP_CP_MEDIA_DOMAIN}${serviceIcon}`}
        width="38"
        height="38"
      />
      <IconLabel dangerouslySetInnerHTML={{ __html: name }} />
    </IconContainer>
  );
};

const Name = styled.span`
  display: inline-block;
  padding: 0 0 0px;
  width: 100%;
  color: #fd980f;
  font-size: 1.125rem;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 6px;
  font-weight: normal;
  vertical-align: baseline;
  background: transparent;
`;

const Address = styled.span`
  display: inline-block;
  color: #444444;
  width: 100%;
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: normal;
  vertical-align: baseline;
  background: transparent;
`;

const Phone = styled.span`
  padding: 11px 0;
  color: #222222;
  font-size: 0.875rem;
  display: inline-block;
  vertical-align: middle;
  line-height: 1;
  font-weight: normal;
`;

const DirectionsButton = styled.button`
  border: 2px #fd980f solid;
  display: block;
  text-align: center;
  font-size: 18px;
  border-radius: 6px;
  padding: 5px;
  text-decoration: none;
  margin-top: 12px;
  color: #fd980f;
  position: relative;
  line-height: inherit;
  vertical-align: baseline;
  background: transparent;
  width: 100%;

  img {
    height: 25px;
    width: 25px;
    position: absolute;
    top: 3px;
    left: 8px;
    display: block;
  }
`;

const ButtonLabel = styled.h5`
  font-size: 1.2rem;
  margin: 0;
  font-weight: 400;
  align-self: center;
`;

const ArrowButton = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 23px;
  margin-top: 10px;
  background: #fd980f url(${icon.nextwhite}) no-repeat center;
  background-size: auto 55%;
`;

export default function DesktopInfoDisplay({ marker, onMarkerClick }) {
  const { t, i18n } = useTranslation();
  const { services } = useParkingService();
  const { id, image, name, address, phone = 'N/A', coordinates, features } = marker;
  const { latitude, longitude } = coordinates || {};

  const displayFeatures =
    services && features && features.length > 0
      ? services.filter(
          ({ id: sid, icon: serviceIcon, isShowIcon }) =>
            features.includes(sid) && serviceIcon && serviceIcon.length > 0 && isShowIcon
        )
      : [];

  return (
    <InfoWindow
      onCloseClick={() => onMarkerClick(undefined)}
      options={{ disableAutoPan: true, pixelOffset: { width: 180, height: 180 } }}
      position={{
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
      }}
    >
      <InfoContainer>
        <div style={{ overflow: 'hidden', borderTopLeftRadius: 8 }}>
          <img
            src={image ? `${process.env.REACT_APP_CP_MEDIA_DOMAIN}${image}` : logo}
            alt=""
            width="100%"
          />
        </div>
        <div style={{ padding: 15 }}>
          <ClickableInfo to={`/${i18n.language}/car_parks/detail/${id}`} role="button">
            <Name>{name}</Name>
            <Address>{address}</Address>

            {/* <ArrowButton /> */}
          </ClickableInfo>

          <Phone>
            {`${t('Map.Phone')}`}
            <span style={{ marginLeft: 20 }}>{phone}</span>
          </Phone>

          {displayFeatures.length > 0 && (
            <>
              <hr style={{ marginTop: 0 }} />
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {displayFeatures.map(({ type, icon: serviceIcon }) => (
                  <ServiceIcon key={type} serviceIcon={serviceIcon} name={type} />
                ))}
              </div>
            </>
          )}

          <DirectionsButton
            onClick={() =>
              window.open(
                `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`
              )
            }
          >
            <img alt="" src={directions} />
            {t('Map.Directions')}
          </DirectionsButton>
        </div>
      </InfoContainer>
    </InfoWindow>
  );
}
